/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/alt-text */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";
import { Button, Form, Modal } from "react-bootstrap";
import Moment from "react-moment";
import { toast } from "react-toastify";
import WriteLogo from "../../assets/images/write.svg";
import DeleteLogo from "../../assets/images/delete.svg";
import {
  addList,
  getList,
  getListById,
  updateList,
} from "../../services/services";
import { ENDPOINT } from "../../services/api-endpoint";
import AppLoader from "../../components/AppLoader";
import { usePageLoader } from "../../services/usePageLoader";
import { useConfirmDialog } from "../../services/useConfirmDialog";
import MesEmptyState from "../../components/mes-empty-state/mes-empty-state";
import "./mes-work-centers.scss";

type CatalogFamily = {
  id: string;
  name: string;
  domain: string;
  display_short_name: string;
  icon_emoji?: string;
  types?: { id: number; slug: string; name: string }[];
};

type CatalogBrand = {
  id: string;
  slug: string;
  name: string;
};

type CatalogModel = {
  id: string;
  name: string;
};

type WorkRow = {
  id: number;
  workcentername?: string | null;
  machinetypeid?: number | null;
  machineFamilyId?: string | null;
  machine_family_id?: string | null;
  catalogTypeId?: number | null;
  catalog_type_id?: number | null;
  catalogBrandId?: string | null;
  catalog_brand_id?: string | null;
  catalogModelId?: string | null;
  catalog_model_id?: string | null;
  machineTypeName?: string | null;
  catalogTechnologyName?: string | null;
  catalogModelName?: string | null;
  iotdevicecode?: string | null;
  iotdevice?: string | null;
  createdate?: string;
  active?: boolean;
};

/** Demo IoT codes — assigned rows appear disabled (per v2 hint). */
const IOT_DEVICE_OPTIONS: { code: string; disabled: boolean }[] = [
  { code: "IOT-MTI-001", disabled: true },
  { code: "IOT-DMG-002", disabled: true },
  { code: "IOT-VML-003", disabled: false },
  { code: "IOT-LAS-004", disabled: false },
  { code: "IOT-CMM-005", disabled: false },
  { code: "IOT-GRD-006", disabled: false },
  { code: "IOT-ASM-007", disabled: false },
  { code: "IOT-HT-008", disabled: false },
];

function normalizeWorkById(raw: unknown): WorkRow | null {
  if (!raw) return null;
  if (Array.isArray(raw)) return (raw[0] as WorkRow) ?? null;
  return raw as WorkRow;
}

function iotDisplay(row: WorkRow): string {
  const c = (row.iotdevicecode ?? "").trim();
  if (c) return c;
  const leg = row.iotdevice != null ? String(row.iotdevice).trim() : "";
  return leg || "—";
}

const MesWorkCenter = () => {
  const { isLoading, runWithLoading } = usePageLoader();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const [validated, setValidated] = useState(false);
  const [busyLabel, setBusyLabel] = useState("Loading machines…");

  const [formInputs, setForm] = useState({
    workcentername: "",
    machineFamilyId: "",
    catalogBrandId: "",
    catalogModelId: "",
    iotdevicecode: "",
  });
  const [editId, setEditId] = useState<number | "">("");
  const [workCollection, setWorkList] = useState<WorkRow[]>([]);
  const [catalogFamilies, setCatalogFamilies] = useState<CatalogFamily[]>([]);
  const [catalogBrands, setCatalogBrands] = useState<CatalogBrand[]>([]);
  const [catalogModels, setCatalogModels] = useState<CatalogModel[]>([]);

  const [totalCount, setTotalCount] = useState<number>(0);
  const [pageSize] = useState<number>(10);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>("");
  const [openAddUpdateModal, setAddUpdateModal] = useState<boolean>(false);

  const searchDebounceRef = useRef<number>();

  const emptyForm = () => ({
    workcentername: "",
    machineFamilyId: "",
    catalogBrandId: "",
    catalogModelId: "",
    iotdevicecode: "",
  });

  const fetchCapabilitiesCatalog = useCallback(async () => {
    try {
      const raw = await getList(ENDPOINT.MACHINE_CAPABILITIES_CATALOG);
      const families = Array.isArray((raw as { families?: CatalogFamily[] })?.families)
        ? (raw as { families: CatalogFamily[] }).families
        : Array.isArray(raw)
          ? (raw as CatalogFamily[])
          : [];
      setCatalogFamilies(families);
    } catch (e) {
      console.log(`Error loading capabilities catalog: ${e}`);
      toast.error("Could not load machine catalog.");
    }
  }, []);

  const fetchBrandsForFamily = useCallback(async (familyId: string) => {
    if (!familyId) {
      setCatalogBrands([]);
      return;
    }
    try {
      const raw = await getList(ENDPOINT.MACHINE_CATALOG_BRANDS, { family_id: familyId });
      const brands = Array.isArray((raw as { brands?: CatalogBrand[] })?.brands)
        ? (raw as { brands: CatalogBrand[] }).brands
        : [];
      setCatalogBrands(brands);
    } catch (e) {
      console.log(`Error loading catalog brands: ${e}`);
      setCatalogBrands([]);
    }
  }, []);

  const fetchModelsForBrand = useCallback(
    async (brandId: string, familyId: string) => {
      if (!brandId || !familyId) {
        setCatalogModels([]);
        return;
      }
      try {
        const raw = await getList(ENDPOINT.MACHINE_CATALOG_MODELS, {
          brand_id: brandId,
          manufacturing_technology_id: familyId,
        });
        const models = Array.isArray((raw as { models?: CatalogModel[] })?.models)
          ? (raw as { models: CatalogModel[] }).models
          : [];
        setCatalogModels(models);
      } catch (e) {
        console.log(`Error loading catalog models: ${e}`);
        setCatalogModels([]);
      }
    },
    [],
  );

  const handleClose = () => {
    setValidated(false);
    setEditId("");
    setForm(emptyForm());
    setCatalogBrands([]);
    setCatalogModels([]);
    setAddUpdateModal(false);
  };

  const handleShow = () => {
    setValidated(false);
    setEditId("");
    setForm(emptyForm());
    setCatalogBrands([]);
    setCatalogModels([]);
    void fetchCapabilitiesCatalog();
    setAddUpdateModal(true);
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    if (name === "machineFamilyId") {
      setForm((prev) => ({
        ...prev,
        machineFamilyId: value,
        catalogBrandId: "",
        catalogModelId: "",
      }));
      void fetchBrandsForFamily(value);
      setCatalogModels([]);
      return;
    }
    if (name === "catalogBrandId") {
      setForm((prev) => ({
        ...prev,
        catalogBrandId: value,
        catalogModelId: "",
      }));
      void fetchModelsForBrand(value, formInputs.machineFamilyId);
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onLoad = useCallback(
    async (nextPageIndex: number = 0, overrideSearchText?: string) => {
      try {
        await runWithLoading(async () => {
          const effectiveSearchText =
            overrideSearchText !== undefined ? overrideSearchText : searchText;
          const request = {
            pagesize: pageSize,
            pagenumber: nextPageIndex,
            serachtext: effectiveSearchText,
          };
          const resp = await getList(ENDPOINT.WORK_CENTER_LIST, request);
          const items = Array.isArray(resp) ? resp : (resp as { data?: WorkRow[] })?.data || [];
          const count = Array.isArray(resp)
            ? items.length
            : Number((resp as { count?: number })?.count || 0);
          setWorkList(items as WorkRow[]);
          setTotalCount(count);
          setPageIndex(nextPageIndex);
        });
      } catch (e) {
        console.log(`Error loading machines: ${e}`);
        toast.error("Could not load machines.");
      }
    },
    [searchText, pageSize, runWithLoading],
  );

  useEffect(() => {
    void onLoad(0, "");
    void fetchCapabilitiesCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scheduleSearchReload = useCallback(
    (value: string) => {
      window.clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = window.setTimeout(() => {
        void onLoad(0, value);
      }, 320);
    },
    [onLoad],
  );

  const onSave = async () => {
    const creating = editId === "";
    setBusyLabel(creating ? "Saving machine…" : "Updating machine…");
    try {
      await runWithLoading(async () => {
        const request: Record<string, unknown> = {
          workcentername: formInputs.workcentername.trim(),
          machine_family_id: formInputs.machineFamilyId || null,
          catalog_type_id: null,
          catalog_brand_id: formInputs.catalogBrandId || null,
          catalog_model_id: formInputs.catalogModelId || null,
          iotdevicecode: (formInputs.iotdevicecode || "").trim() || null,
          iotdevice: null,
          active: true,
          operationIds: [],
        };
        if (!creating) {
          request.id = editId;
        }
        let resp;
        if (creating) {
          resp = await addList(ENDPOINT.WORK_CENTER_ADD, request);
        } else {
          resp = await updateList(ENDPOINT.WORK_CENTER_UPDATE, request);
        }
        if (resp === "success") {
          toast.success(
            creating ? "Machine saved successfully." : "Machine updated successfully.",
          );
          handleClose();
          await onLoad(creating ? 0 : pageIndex, searchText);
        } else {
          toast.error(creating ? "Could not save machine." : "Could not update machine.");
        }
      });
    } catch (e) {
      console.log(`Error saving machine: ${e}`);
      toast.error("Could not save machine.");
    } finally {
      setBusyLabel("Loading machines…");
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    const form = e.currentTarget;
    e.preventDefault();
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }
    void onSave();
  };

  const onEdit = async (id: number) => {
    try {
      const raw = await runWithLoading(async () => {
        await fetchCapabilitiesCatalog();
        return getListById(ENDPOINT.WORK_CENTER_LIST_BY_ID, id);
      });
      const row = normalizeWorkById(raw);
      if (!row) {
        toast.error("Machine not found.");
        return;
      }
      const familyId = String(row.machineFamilyId ?? row.machine_family_id ?? "").trim();
      const brandId = String(row.catalogBrandId ?? row.catalog_brand_id ?? "").trim();
      if (familyId) {
        await fetchBrandsForFamily(familyId);
      }
      if (brandId && familyId) {
        await fetchModelsForBrand(brandId, familyId);
      }
      setEditId(row.id);
      setForm({
        workcentername: row.workcentername ?? "",
        machineFamilyId: familyId,
        catalogBrandId: brandId,
        catalogModelId: String(row.catalogModelId ?? row.catalog_model_id ?? "").trim(),
        iotdevicecode: (row.iotdevicecode ?? "").trim() || iotDisplay(row) || "",
      });
      setAddUpdateModal(true);
    } catch (e) {
      console.log(`Error loading machine: ${e}`);
      toast.error("Could not load machine.");
    }
  };

  const onDelete = async (row: WorkRow) => {
    const ok = await confirm();
    if (!ok) return;
    try {
      const resp = await updateList(ENDPOINT.WORK_CENTER_UPDATE, {
        id: row.id,
        active: false,
      });
      if (resp === "success") {
        toast.success("Machine removed.");
        await onLoad(pageIndex, searchText);
      } else {
        toast.error("Could not delete machine.");
      }
    } catch (e) {
      console.log(`Error delete machine: ${e}`);
      toast.error("Could not delete machine.");
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const showPagination = totalPages > 1;
  const hasRecords = workCollection.length > 0;

  const saveDisabled =
    !formInputs.workcentername.trim() ||
    !formInputs.machineFamilyId ||
    !formInputs.iotdevicecode;

  return (
    <div className="machines-page">
      {isLoading && <AppLoader text={busyLabel} />}
      <div className="machines-catalog">
          <div className="machines-catalog__shell">
            <div className="machines-catalog__head">
              <button
                type="button"
                className="machines-catalog__add-btn"
                onClick={handleShow}
              >
                <i className="fa fa-plus" aria-hidden />
                Add Machine
              </button>
            </div>

            <div className="machines-catalog__search-wrap">
              <i className="fa fa-search machines-catalog__search-icon" aria-hidden />
              <input
                type="search"
                className="machines-catalog__search-input"
                placeholder="Search Machines"
                value={searchText}
                onChange={(e) => {
                  const v = e.target.value;
                  setSearchText(v);
                  scheduleSearchReload(v);
                }}
                aria-label="Search machines"
              />
            </div>

            {!hasRecords ? (
              !isLoading && (
                <MesEmptyState
                  icon="fa-microchip"
                  title="No machines found"
                  subtitle="Add equipment and assign IoT devices for shop-floor capacity."
                />
              )
            ) : (
              <>
                <div className="machines-catalog__table-wrap">
                  <table className="machines-catalog__table">
                    <thead>
                      <tr>
                        <th>Machine name</th>
                        <th>Type</th>
                        <th>IoT device</th>
                        <th>Created on</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workCollection.map((row) => {
                        const typeName =
                          (row.catalogModelName ?? "").trim() ||
                          (row.catalogTechnologyName ?? "").trim() ||
                          (row.machineTypeName ?? "").trim() ||
                          "—";
                        return (
                          <tr key={row.id}>
                            <td className="machines-catalog__cell-name">
                              {row.workcentername ?? "—"}
                            </td>
                            <td className="machines-catalog__cell-muted">{typeName}</td>
                            <td className="machines-catalog__cell-muted">{iotDisplay(row)}</td>
                            <td className="machines-catalog__cell-muted">
                              {row.createdate ? (
                                <Moment format="D MMM YYYY">{row.createdate}</Moment>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td>
                              <div className="machines-catalog__actions">
                                <button
                                  type="button"
                                  className="machines-catalog__icon-btn"
                                  title="Edit"
                                  onClick={() => void onEdit(row.id)}
                                >
                                  <img src={WriteLogo} alt="" />
                                </button>
                                <button
                                  type="button"
                                  className="machines-catalog__icon-btn machines-catalog__icon-btn--danger"
                                  title="Delete"
                                  onClick={() => void onDelete(row)}
                                >
                                  <img src={DeleteLogo} alt="" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {showPagination && (
                  <div className="machines-catalog__pagination">
                    <nav aria-label="Machines pagination">
                      <ul className="pagination">
                        <li className={`page-item ${pageIndex === 0 ? "disabled" : ""}`}>
                          <button
                            type="button"
                            className="page-link"
                            disabled={pageIndex === 0}
                            onClick={() => void onLoad(pageIndex - 1)}
                          >
                            &laquo;
                          </button>
                        </li>
                        {Array.from({ length: totalPages }).map((_, i) => (
                          <li
                            key={i}
                            className={`page-item ${i === pageIndex ? "active" : ""}`}
                          >
                            <button
                              type="button"
                              className="page-link"
                              onClick={() => void onLoad(i)}
                            >
                              {i + 1}
                            </button>
                          </li>
                        ))}
                        <li
                          className={`page-item ${
                            pageIndex >= totalPages - 1 ? "disabled" : ""
                          }`}
                        >
                          <button
                            type="button"
                            className="page-link"
                            disabled={pageIndex >= totalPages - 1}
                            onClick={() => void onLoad(pageIndex + 1)}
                          >
                            &raquo;
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

      <Modal
        className="machines-modal"
        size="lg"
        centered
        show={openAddUpdateModal}
        onHide={handleClose}
        backdrop="static"
      >
        <Modal.Header closeButton={false} className="machines-modal__header">
          <Modal.Title as="h2" className="machines-modal__title">
            {editId === "" ? "Add Machine" : "Edit Machine"}
          </Modal.Title>
          <button
            type="button"
            className="machines-modal__close"
            aria-label="Close"
            onClick={handleClose}
          >
            <i className="fa fa-times" aria-hidden />
          </button>
        </Modal.Header>
        <Modal.Body className="machines-modal__body">
          <Form
            noValidate
            validated={validated}
            onSubmit={handleSubmit}
            id="machines-form"
          >
            <Form.Group
              className="mb-3 machines-modal__field-group"
              controlId="workcentername"
            >
              <Form.Label className="machines-modal__field-label">Machine name</Form.Label>
              <Form.Control
                required
                type="text"
                name="workcentername"
                className="machines-modal__text-input"
                placeholder="e.g. Mazak Quick Turn 250"
                value={formInputs.workcentername}
                onChange={handleChange}
              />
              <Form.Control.Feedback type="invalid">
                Machine name is required.
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3 machines-modal__field-group" controlId="machineFamilyId">
              <Form.Label className="machines-modal__field-label">Machine family</Form.Label>
              <Form.Control
                as="select"
                name="machineFamilyId"
                className="form-control custom-select machines-modal__select"
                value={formInputs.machineFamilyId}
                onChange={handleChange}
                required
                aria-label="Machine family"
                isInvalid={validated && !String(formInputs.machineFamilyId).trim()}
              >
                <option value="">Select machine family</option>
                {catalogFamilies.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.icon_emoji ? `${f.icon_emoji} ` : ""}
                    {f.display_short_name || f.name}
                    {f.domain ? ` · ${f.domain}` : ""}
                  </option>
                ))}
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                Select a machine family.
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3 machines-modal__field-group" controlId="catalogBrandId">
              <Form.Label className="machines-modal__field-label">
                Catalog brand <span className="text-muted">(optional)</span>
              </Form.Label>
              <Form.Control
                as="select"
                name="catalogBrandId"
                className="form-control custom-select machines-modal__select"
                value={formInputs.catalogBrandId}
                onChange={handleChange}
                disabled={!formInputs.machineFamilyId}
                aria-label="Catalog brand"
              >
                <option value="">
                  {formInputs.machineFamilyId ? "Optional" : "Select a family first"}
                </option>
                {catalogBrands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group className="mb-3 machines-modal__field-group" controlId="catalogModelId">
              <Form.Label className="machines-modal__field-label">
                Catalog model <span className="text-muted">(optional)</span>
              </Form.Label>
              <Form.Control
                as="select"
                name="catalogModelId"
                className="form-control custom-select machines-modal__select"
                value={formInputs.catalogModelId}
                onChange={handleChange}
                disabled={!formInputs.catalogBrandId}
                aria-label="Catalog model"
              >
                <option value="">
                  {formInputs.catalogBrandId ? "Optional" : "Select a brand first"}
                </option>
                {catalogModels.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group
              className="mb-2 machines-modal__field-group"
              controlId="iotdevicecode"
            >
              <Form.Label className="machines-modal__field-label">IoT device</Form.Label>
              <Form.Control
                as="select"
                name="iotdevicecode"
                className="form-control custom-select machines-modal__select"
                value={formInputs.iotdevicecode}
                onChange={handleChange}
                required
                aria-label="IoT device"
                isInvalid={validated && !String(formInputs.iotdevicecode).trim()}
              >
                <option value="">Select IoT device</option>
                {IOT_DEVICE_OPTIONS.map((o) => (
                  <option key={o.code} value={o.code} disabled={o.disabled}>
                    {o.code}
                    {o.disabled ? " (assigned)" : ""}
                  </option>
                ))}
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                Select an IoT device.
              </Form.Control.Feedback>
              <p className="machines-modal__hint">
                Assigned devices are shown but disabled. Select an available device.
              </p>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="machines-modal__footer">
          <Button
            type="button"
            variant="light"
            className="machines-modal__btn-cancel"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="machines-form"
            className="machines-modal__btn-save"
            disabled={saveDisabled}
          >
            Save Machine
          </Button>
        </Modal.Footer>
      </Modal>
      <ConfirmDialog />
    </div>
  );
};

export default MesWorkCenter;
